// ============================================================================
// GERENCIAMENTO DE FILIADOS — UNIFICADO
// Arquivo: src/components/Dashboard_ADM/GerenciamentoProfissionais.tsx
// UPDATED: 2026-04-16 — v4.4
//
// ✅ FIX: CPF/CNPJ strip de formatação antes de enviar para a API
// ✅ NOVO: Aba "Documentos" no modal Editar — exibe docs existentes + upload
// ✅ NOVO: Paginação client-side na tabela (20 por página)
// ✅ NOVO: Campo Nº FEPCSSP no modal de edição
// ✅ FIX v4.4: Modal editar agora inclui Nome do Responsável, Data de Nascimento
//             e Complemento — espelhando o modal de cadastro
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search, Filter, UserCog, Mail, Phone, MapPin, Building2, Edit,
  Trash2, Eye, Plus, Download, CheckCircle, XCircle, AlertCircle,
  Loader2, AlertTriangle, Clock, X, Save, Hash, Calendar,
  FileText, User, Home, Image, ExternalLink, RefreshCw, MessageSquare, Star,
  Upload, Trash, ChevronLeft, ChevronRight,
} from 'lucide-react';
import api from '../../services/api';
import ModalCadastroFiliado from './ModalCadastroFiliado';
import { glass, text } from './ui/theme';
import GlassBackdrop from './ui/GlassBackdrop';

// ─── Types ───────────────────────────────────────────────────────────────────
interface Filiado {
  id: string;
  systemId: string;
  nome: string;
  nomeResponsavel: string;
  email: string;
  telefone: string;
  cpf: string;
  cnpj: string;
  cidade: string;
  estado: string;
  endereco: string;
  cep: string;
  status: 'ativo' | 'inativo' | 'suspenso' | 'pendente';
  dataCadastro: string;
  descricao: string;
  logoUrl?: string;
  isSchool: boolean;
  website: string;
  registroFepcssp?: string;
  identidadeInternacional?: string;
  // ✅ v4.4: campos adicionados para edição completa
  dataNascimento?: string;
  metadata?: {
    funcao?: string | null;
    experienciaAnos?: number | null;
    dataInicioAtividade?: string | null;
    complemento?: string | null;
  } | null;
}

interface Documento {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  documentType: string;
  description?: string;
  url: string;
  uploadedAt: string;
}

// ─── Upload Slot (para o modal de edição) ────────────────────────────────────
interface UploadSlot {
  arquivo: File | null;
  preview: string | null;
  url: string | null;
  enviado: boolean;
  enviando: boolean;
  erro: string | null;
}
const slotVazio = (): UploadSlot => ({
  arquivo: null, preview: null, url: null,
  enviado: false, enviando: false, erro: null,
});

// ─── Mapper ──────────────────────────────────────────────────────────────────
const mapFiliadoStatus = (s: string): Filiado['status'] => {
  const m: Record<string, Filiado['status']> = {
    active: 'ativo', inactive: 'inativo', pending: 'pendente', suspended: 'suspenso',
    ativo: 'ativo', inativo: 'inativo', pendente: 'pendente', suspenso: 'suspenso',
  };
  return m[s] ?? 'pendente';
};

const mapFiliado = (a: any): Filiado => ({
  id:               a.id,
  systemId:         a.systemId     ?? a.system_id  ?? '',
  nome:             a.schoolName   ?? a.nomeSolicitante ?? a.fullName ?? '',
  nomeResponsavel:  a.responsibleName ?? a.user?.fullName ?? a.nomeSolicitante ?? a.ownerName ?? '',
  email:            a.email        ?? '',
  telefone:         a.phone        ?? a.telefone   ?? '',
  cpf:              a.cpf          ?? '',
  cnpj:             a.cnpj         ?? '',
  cidade:           a.city         ?? a.cidade     ?? '',
  estado:           a.state        ?? a.estado     ?? '',
  endereco:         a.address      ?? a.endereco   ?? '',
  cep:              a.postalCode   ?? a.cep        ?? '',
  status:           mapFiliadoStatus(a.status ?? ''),
  dataCadastro:     a.createdAt    ?? a.dataCadastro ?? '',
  descricao:        a.description  ?? a.especializacoes ?? '',
  logoUrl:          a.logoUrl      ?? undefined,
  isSchool:         a.isSchool     ?? a.is_school ?? false,
  website:          a.website      ?? '',
  registroFepcssp:  a.registroFepcssp ?? a.registro_fepcssp ?? '',
  identidadeInternacional: a.identidadeInternacional ?? a.identidade_internacional ?? '',
  // ✅ v4.4
  dataNascimento:   a.dataNascimento ?? a.data_nascimento ?? '',
  metadata:         a.metadata     ?? null,
});

// ─── Helpers ─────────────────────────────────────────────────────────────────
const inputCls = 'w-full px-4 py-2.5 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 text-sm font-medium bg-white';
const labelCls = 'block text-xs font-bold text-gray-500 mb-1 uppercase tracking-wide';

const POR_PAGINA = 20;

const getErrorMessage = (error: any, fallback = 'Erro na requisição'): string => {
  const data = error?.response?.data;
  if (Array.isArray(data?.message))      return data.message.join(' | ');
  if (typeof data?.message === 'string') return data.message;
  if (typeof error?.message === 'string')return error.message;
  return fallback;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024)         return `${bytes} B`;
  if (bytes < 1024 * 1024)  return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isImage = (mimeType: string) => mimeType.startsWith('image/');

const DOC_TYPE_LABEL: Record<string, string> = {
  cpf: 'CPF', rg: 'RG/Identidade', comprovante: 'Comprovante de Endereço',
  diploma: 'Diploma', certificado: 'Certificado', foto: 'Foto / Logo',
  homologacao: 'Contrato Social', outro: 'Outro',
};

// ─── Avatar ──────────────────────────────────────────────────────────────────
type ModalTab = 'info' | 'documentos' | 'pagamento';
type EditTab  = 'dados' | 'documentos';

const FiliadoAvatar: React.FC<{ filiado: Filiado; size?: 'sm' | 'md' }> = ({ filiado, size = 'sm' }) => {
  const [imgError, setImgError] = useState(false);
  const sz = size === 'sm' ? 'w-10 h-10 text-base' : 'w-16 h-16 text-2xl';

  return filiado.logoUrl && !imgError ? (
    <img
      src={filiado.logoUrl}
      alt={filiado.nome}
      className={`${sz} rounded-full object-cover border border-cosmos-100 flex-shrink-0`}
      onError={() => setImgError(true)}
    />
  ) : (
    <div className={`${sz} rounded-full bg-gradient-gold flex items-center justify-center text-cosmos-900 font-semibold flex-shrink-0`}>
      {filiado.nome.charAt(0)}
    </div>
  );
};

// ─── Component ───────────────────────────────────────────────────────────────
const GerenciamentoProfissionais: React.FC = () => {
  const [filiados, setFiliados]             = useState<Filiado[]>([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState<string | null>(null);
  const [busca, setBusca]                   = useState('');
  const [buscaInput, setBuscaInput]         = useState('');
  const [filtroStatus, setFiltroStatus]     = useState<string>('todos');
  const [filtroEstado, setFiltroEstado]     = useState<string>('todos');
  const [paginaAtual, setPaginaAtual]       = useState(1);
  const [selecionado, setSelecionado]       = useState<Filiado | null>(null);
  const [modalVer, setModalVer]             = useState(false);
  const [modalTab, setModalTab]             = useState<ModalTab>('info');
  const [modalEditar, setModalEditar]       = useState(false);
  const [modalEditarTab, setModalEditarTab] = useState<EditTab>('dados');
  const [modalCadastro, setModalCadastro]   = useState(false);
  const [modalRejeitar, setModalRejeitar]   = useState<Filiado | null>(null);
  const [motivoRejeicao, setMotivoRejeicao] = useState('');
  const [salvando, setSalvando]             = useState(false);
  const [totalRegistros, setTotalRegistros] = useState(0);

  // Documentos do filiado selecionado
  const [documentos, setDocumentos]         = useState<Documento[]>([]);
  const [loadingDocs, setLoadingDocs]       = useState(false);

  // Upload de documento no modal editar
  const [slotEditDoc, setSlotEditDoc]           = useState<UploadSlot>(slotVazio());
  const [tipoDocEdit, setTipoDocEdit]           = useState('cpf');
  const [descricaoDocEdit, setDescricaoDocEdit] = useState('');
  const [uploadingEditDoc, setUploadingEditDoc] = useState(false);

  // Form edição — ✅ v4.4: adicionados nomeResponsavel, dataNascimento, complemento
  const [formEdit, setFormEdit] = useState<Partial<Filiado & { complemento: string }>>({});
  const [erroEdit, setErroEdit] = useState<string | null>(null);
  const [logoError, setLogoError] = useState(false);
  // Pagamento no modal editar
const [paymentValueInput, setPaymentValueInput] = useState('');
const [salvandoPagamento, setSalvandoPagamento] = useState(false);
const [erroPagamento,     setErroPagamento]     = useState<string | null>(null);
const [sucPagamento,      setSucPagamento]      = useState<string | null>(null);
  useEffect(() => {
    const t = setTimeout(() => setBusca(buscaInput), 400);
    return () => clearTimeout(t);
  }, [buscaInput]);

  // Reset página ao mudar filtros
  useEffect(() => { setPaginaAtual(1); }, [busca, filtroStatus, filtroEstado]);

  // ─── Fetch filiados ───────────────────────────────────────────────────────
  const fetchFiliados = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      let page = 1;
      const PAGE_SIZE = 200;
      let todos: any[] = [];
      let total = 0;

      while (true) {
        const response = await api.get<any>(`/affiliates?page=${page}&limit=${PAGE_SIZE}`);
        const lista: any[] = response?.affiliates ?? response?.results ?? [];
        total = response?.total ?? 0;
        todos = [...todos, ...lista];
        if (lista.length < PAGE_SIZE || todos.length >= total) break;
        page++;
      }

     const lista = todos.map(mapFiliado);
setFiliados(lista);
setTotalRegistros(total);
// ✅ Sincroniza selecionado com os dados frescos
setSelecionado(prev => prev ? (lista.find(f => f.id === prev.id) ?? prev) : null);
    } catch (e: any) {
      setError(getErrorMessage(e, 'Erro ao carregar filiados'));
    } finally { setLoading(false); }
  }, [busca]);

  useEffect(() => { fetchFiliados(); }, [fetchFiliados]);

  // ─── Fetch documentos ────────────────────────────────────────────────────
  const fetchDocumentos = useCallback(async (affiliateId: string) => {
    setLoadingDocs(true);
    setDocumentos([]);
    try {
      const data: any = await api.get(`/documents/affiliate/${affiliateId}`);
      const docs: Documento[] = Array.isArray(data) ? data : [];
      setDocumentos(docs);

      const logoDoc = docs.find(d =>
        d.description?.toLowerCase().includes('logo') ||
        (d.documentType === 'foto' && d.description?.toLowerCase().includes('escola'))
      );

      if (logoDoc?.url) {
        setLogoError(false);
        setSelecionado(prev => prev ? { ...prev, logoUrl: logoDoc.url } : prev);
        setFiliados(prev =>
          prev.map(f => f.id === affiliateId ? { ...f, logoUrl: logoDoc.url } : f)
        );
      }
    } catch {
      setDocumentos([]);
    } finally { setLoadingDocs(false); }
  }, []);

  const abrirModal = (f: Filiado) => {
    setSelecionado(f);
    setModalTab('info');
    setModalVer(true);
    fetchDocumentos(f.id);
    setLogoError(false);
  };

  // ─── Editar ───────────────────────────────────────────────────────────────
  // ✅ v4.4: populando todos os campos novos
  const abrirEditar = (f: Filiado) => {
    setSelecionado(f);
    setFormEdit({
      nome:             f.nome,
      nomeResponsavel:  f.nomeResponsavel ?? '',
      email:            f.email,
      telefone:         f.telefone,
      cpf:              f.cpf?.replace(/\D/g, '') ?? '',
      cnpj:             f.cnpj?.replace(/\D/g, '') ?? '',
      endereco:         f.endereco,
      cep:              f.cep?.replace(/\D/g, '') ?? '',
      cidade:           f.cidade,
      estado:           f.estado,
      descricao:        f.descricao,
      status:           f.status,
      website:          f.website,
      isSchool:         f.isSchool,
      registroFepcssp:  f.registroFepcssp ?? '',
      identidadeInternacional: f.identidadeInternacional ?? '',
      dataNascimento:   f.dataNascimento ?? '',
      complemento:      f.metadata?.complemento ?? '',
    });
    setErroEdit(null);
    setModalEditarTab('dados');
    setSlotEditDoc(slotVazio());
    setTipoDocEdit('cpf');
    setDescricaoDocEdit('');
    fetchDocumentos(f.id);
    setModalEditar(true);
  };

  const STATUS_EN: Record<string, string> = {
    ativo: 'active', inativo: 'inactive', pendente: 'pending', suspenso: 'suspended',
  };

  const handleSalvarEdicao = async () => {
    if (!selecionado) return;
    if (formEdit.identidadeInternacional && !/^[A-Z0-9]{15}$/.test(formEdit.identidadeInternacional)) {
      setErroEdit('Identidade internacional deve conter exatamente 15 letras ou números');
      return;
    }
    setSalvando(true); setErroEdit(null);
    try {
      // ✅ v4.4: payload inclui responsibleName, dataNascimento e complemento via metadata
      const payload: Record<string, any> = {
        schoolName:       formEdit.nome             || undefined,
        responsibleName:  formEdit.nomeResponsavel  || undefined,
        email:            formEdit.email            || undefined,
        phone:            formEdit.telefone         || undefined,
        cpf:              formEdit.cpf?.replace(/\D/g, '')  || undefined,
        cnpj:             formEdit.cnpj?.replace(/\D/g, '') || undefined,
        address:          formEdit.endereco         || undefined,
        postalCode:       formEdit.cep?.replace(/\D/g, '') || undefined,
        city:             formEdit.cidade           || undefined,
        state:            formEdit.estado           || undefined,
        description:      formEdit.descricao        || undefined,
        website:          formEdit.website          || undefined,
        isSchool:         formEdit.isSchool         ?? false,
        registroFepcssp:  formEdit.registroFepcssp?.trim() || undefined,
        identidadeInternacional: formEdit.identidadeInternacional?.trim().toUpperCase() || undefined,
        dataNascimento:   formEdit.dataNascimento   || undefined,
      };

      // Complemento vai dentro de metadata (merge com o existente)
      if (formEdit.complemento !== undefined) {
        payload.metadata = {
          ...(selecionado.metadata ?? {}),
          complemento: formEdit.complemento || null,
        };
      }

      if (formEdit.status) payload.status = STATUS_EN[formEdit.status] ?? formEdit.status;
      Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);
      await api.patch(`/affiliates/${selecionado.id}`, payload);
      setModalEditar(false);
      await fetchFiliados();
    } catch (e: any) {
      setErroEdit(getErrorMessage(e, 'Erro desconhecido ao salvar'));
    } finally { setSalvando(false); }
  };

  // ─── Upload de documento no modal editar ──────────────────────────────────
  const handleUploadDocEdit = async () => {
    if (!slotEditDoc.arquivo || !selecionado) return;
    setUploadingEditDoc(true);
    setSlotEditDoc(p => ({ ...p, enviando: true, erro: null }));
    try {
      const form = new FormData();
      form.append('file', slotEditDoc.arquivo);
      const desc = descricaoDocEdit.trim() || DOC_TYPE_LABEL[tipoDocEdit] || tipoDocEdit;
      const res: any = await api.post(
        `/documents/public-upload?affiliateId=${encodeURIComponent(selecionado.id)}&documentType=${tipoDocEdit}&description=${encodeURIComponent(desc)}`,
        form,
        { headers: { 'Content-Type': undefined } },
      );
      const url: string = res.url ?? res.fileUrl ?? res.path ?? '';
      if (!url) throw new Error('URL não retornada pelo servidor');

      if (tipoDocEdit === 'foto' && desc.toLowerCase().includes('logo')) {
        await api.patch(`/affiliates/${selecionado.id}`, { logoUrl: url });
        setSelecionado(prev => prev ? { ...prev, logoUrl: url } : prev);
        setFiliados(prev => prev.map(f => f.id === selecionado.id ? { ...f, logoUrl: url } : f));
      }

      setSlotEditDoc(p => ({ ...p, url, enviado: true, enviando: false }));
      await fetchDocumentos(selecionado.id);
      setTimeout(() => {
        setSlotEditDoc(slotVazio());
        setDescricaoDocEdit('');
      }, 2000);
    } catch (e: any) {
      setSlotEditDoc(p => ({ ...p, enviando: false, erro: e.message ?? 'Erro no upload' }));
    } finally { setUploadingEditDoc(false); }
  };

  // ─── Deletar documento ────────────────────────────────────────────────────
  const handleDeletarDocumento = async (docId: string) => {
    if (!window.confirm('Excluir este documento?')) return;
    try {
      await api.delete(`/documents/${docId}`);
      if (selecionado) await fetchDocumentos(selecionado.id);
    } catch (e: any) {
      alert(getErrorMessage(e, 'Erro ao excluir documento'));
    }
  };

  const selecionarArquivoEdit = (file: File) => {
    const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    setSlotEditDoc({ arquivo: file, preview, url: null, enviado: false, enviando: false, erro: null });
  };

  // ─── Aprovar ──────────────────────────────────────────────────────────────
  const handleAprovar = async (id: string) => {
    setSalvando(true);
    try {
      await api.post(`/affiliates/${id}/approve`);
      await fetchFiliados();
      setSelecionado(prev => prev ? { ...prev, status: 'ativo' } : prev);
    } catch (e: any) {
      alert(getErrorMessage(e, 'Erro ao aprovar filiado'));
    } finally { setSalvando(false); }
  };

  // ─── WhatsApp ─────────────────────────────────────────────────────────────
  const handleWhatsApp = (f: Filiado) => {
    const telefone = f.telefone.replace(/\D/g, '');
    const numero   = telefone.startsWith('55') ? telefone : `55${telefone}`;
    const msg = encodeURIComponent(
      `Olá ${f.nomeResponsavel || f.nome}! 👋\n\n` +
      `Sua filiação à Federação foi *aprovada* com sucesso! ✅\n\n` +
      `*Seus dados de acesso:*\n` +
      `• ID: *${f.systemId}*\n` +
      `• E-mail: *${f.email}*\n` +
      `• Senha padrão: *fil@feder26*\n\n` +
      `Acesse o portal do filiado e complete seu perfil.\n` +
      `Qualquer dúvida, estamos à disposição! 🎓`
    );
    window.open(`https://wa.me/${numero}?text=${msg}`, '_blank');
  };

  // ─── Rejeitar ─────────────────────────────────────────────────────────────
  const handleRejeitar = async () => {
    if (!modalRejeitar) return;
    if (!motivoRejeicao.trim()) { alert('Informe o motivo da rejeição'); return; }
    if (motivoRejeicao.trim().length < 10) { alert('O motivo deve ter pelo menos 10 caracteres'); return; }
    setSalvando(true);
    try {
      await api.post(`/affiliates/${modalRejeitar.id}/reject`, { reason: motivoRejeicao });
      setModalRejeitar(null); setMotivoRejeicao('');
      setModalVer(false);
      await fetchFiliados();
    } catch (e: any) {
      alert(getErrorMessage(e, 'Erro ao rejeitar filiado'));
    } finally { setSalvando(false); }
  };

  // ─── Deletar filiado ──────────────────────────────────────────────────────
  const handleDeletar = async (id: string, nome: string) => {
    if (!window.confirm(`Confirmar exclusão de "${nome}"?`)) return;
    setSalvando(true);
    try {
      await api.delete(`/affiliates/${id}`);
      await fetchFiliados();
    } catch (e: any) {
      alert(getErrorMessage(e, 'Erro ao excluir filiado'));
    } finally { setSalvando(false); }
  };

  const handleToggleEscola = async (f: Filiado) => {
    try {
      await api.patch(`/affiliates/${f.id}`, { isSchool: !f.isSchool });
      setFiliados(prev =>
        prev.map(x => x.id === f.id ? { ...x, isSchool: !x.isSchool } : x)
      );
    } catch {
      alert('Erro ao atualizar');
    }
  };
  // ─── Gerar cobrança de pagamento ──────────────────────────────
const handleSalvarPagamento = async () => {
  if (!selecionado) return;
  const valor = parseFloat(paymentValueInput.replace(',', '.'));
  if (!valor || valor <= 0) {
    setErroPagamento('Informe um valor maior que zero.');
    return;
  }
  setSalvandoPagamento(true);
  setErroPagamento(null);
  setSucPagamento(null);
  try {
    await api.post(`/whatsapp-agent/affiliates/${selecionado.id}/payment-link`, {
      paymentValue: valor,
    });
    setSucPagamento('Cobrança gerada e enviada ao candidato pelo WhatsApp.');
    await fetchFiliados();
  } catch (e: any) {
    setErroPagamento(getErrorMessage(e, 'Erro ao gerar cobrança de pagamento'));
  } finally {
    setSalvandoPagamento(false);
  }
};
// ─── Confirmar pagamento ──────────────────────────────────────
const handleConfirmarPagamento = async () => {
  if (!selecionado) return;
  if (!window.confirm(`Confirmar pagamento de ${selecionado.nome}? O acesso será liberado.`)) return;
  setSalvandoPagamento(true);
  setErroPagamento(null);
  setSucPagamento(null);
  try {
    await api.post(`/affiliates/${selecionado.id}/confirm-payment`);
    setSucPagamento('Pagamento confirmado! Acesso liberado.');
    await fetchFiliados();
    setSelecionado(prev => prev ? { ...prev, status: 'ativo' } : prev);
  } catch (e: any) {
    setErroPagamento(getErrorMessage(e, 'Erro ao confirmar pagamento'));
  } finally {
    setSalvandoPagamento(false);
  }
};

// ─── Exportar PDF ─────────────────────────────────────────────
const handleExportarPDFModal = () => {
  if (!selecionado) return;

  const docsHtml = documentos.map(doc => {
    if (isImage(doc.mimeType)) {
      return `
        <div class="doc-item">
          <img src="${doc.url}"
               style="max-width:100%;max-height:350px;object-fit:contain;border-radius:8px;border:1px solid #e5e7eb;"
          />
          <p class="doc-label">
            ${doc.description || doc.fileName}<br/>
            <span>${DOC_TYPE_LABEL[doc.documentType] ?? doc.documentType} · ${formatFileSize(doc.fileSize)}</span>
          </p>
        </div>`;
    }
    return `
      <div class="doc-item doc-file">
        <div style="font-size:36px;">📄</div>
        <p class="doc-label">
          ${doc.description || doc.fileName}<br/>
          <span>${DOC_TYPE_LABEL[doc.documentType] ?? doc.documentType} · ${formatFileSize(doc.fileSize)}</span>
        </p>
      </div>`;
  }).join('');

  const statusColor: Record<string, string> = {
    ativo:    '#065f46,#d1fae5',
    pendente: '#92400e,#fef3c7',
    inativo:  '#374151,#f3f4f6',
    suspenso: '#991b1b,#fee2e2',
  };
  const [stColor, stBg] = (statusColor[selecionado.status] ?? '#374151,#f3f4f6').split(',');

  const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8"/>
  <title>Ficha — ${selecionado.nome}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:Arial,sans-serif;padding:36px;color:#111;font-size:14px;line-height:1.5}
    h1{font-size:22px;font-weight:900;margin-bottom:2px}
    .subtitle{font-size:12px;color:#6b7280;margin-bottom:8px}
    .badge{display:inline-block;padding:3px 12px;border-radius:999px;font-size:11px;font-weight:700;letter-spacing:.5px;color:${stColor};background:${stBg}}
    .section{margin-top:24px}
    .section h2{font-size:11px;font-weight:900;text-transform:uppercase;letter-spacing:1.5px;color:#9ca3af;border-bottom:1px solid #e5e7eb;padding-bottom:6px;margin-bottom:12px}
    .grid{display:grid;grid-template-columns:1fr 1fr;gap:10px 28px}
    .field label{font-size:10px;color:#9ca3af;display:block;margin-bottom:1px;text-transform:uppercase;letter-spacing:.8px}
    .field p{font-size:13px;font-weight:700;color:#111}
    .descricao{font-size:13px;color:#374151;background:#f9fafb;padding:12px;border-radius:8px;margin-top:4px}
    .docs{display:flex;flex-wrap:wrap;gap:20px;margin-top:8px;align-items:flex-start}
    .doc-item{text-align:center;max-width:300px;flex:1 1 250px}
    .doc-file{padding:20px;border:1px dashed #d1d5db;border-radius:8px}
    .doc-label{font-size:11px;color:#6b7280;margin-top:6px;word-break:break-word}
    .doc-label span{font-size:10px;color:#9ca3af}
    .footer{margin-top:36px;font-size:10px;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:12px}
    @media print{body{padding:18px} .doc-item{page-break-inside:avoid}}
  </style>
</head>
<body>
  <h1>${selecionado.nome}</h1>
  <p class="subtitle">
    ID: <strong>${selecionado.systemId}</strong>
    ${selecionado.registroFepcssp ? ' · FEPCSSP: <strong>' + selecionado.registroFepcssp + '</strong>' : ''}
  </p>
  <span class="badge">${selecionado.status.toUpperCase()}</span>

  <div class="section">
    <h2>Identificação</h2>
    <div class="grid">
      <div class="field"><label>Nome do Responsável</label><p>${selecionado.nomeResponsavel || '—'}</p></div>
      <div class="field"><label>CPF</label><p>${selecionado.cpf || '—'}</p></div>
      <div class="field"><label>CNPJ</label><p>${selecionado.cnpj || '—'}</p></div>
      ${selecionado.dataNascimento
        ? `<div class="field"><label>Data de Nascimento</label><p>${new Date(selecionado.dataNascimento + 'T12:00:00').toLocaleDateString('pt-BR')}</p></div>`
        : ''}
    </div>
  </div>

  <div class="section">
    <h2>Contato</h2>
    <div class="grid">
      <div class="field"><label>E-mail</label><p>${selecionado.email || '—'}</p></div>
      <div class="field"><label>Telefone</label><p>${selecionado.telefone || '—'}</p></div>
      ${selecionado.website
        ? `<div class="field"><label>Website / Instagram</label><p>${selecionado.website}</p></div>`
        : ''}
    </div>
  </div>

  <div class="section">
    <h2>Endereço</h2>
    <div class="grid">
      <div class="field"><label>Logradouro</label><p>${selecionado.endereco || '—'}</p></div>
      <div class="field"><label>CEP</label><p>${selecionado.cep || '—'}</p></div>
      <div class="field"><label>Cidade</label><p>${selecionado.cidade || '—'}</p></div>
      <div class="field"><label>Estado</label><p>${selecionado.estado || '—'}</p></div>
      ${selecionado.metadata?.complemento
        ? `<div class="field"><label>Complemento</label><p>${selecionado.metadata.complemento}</p></div>`
        : ''}
    </div>
  </div>

  ${selecionado.descricao ? `
  <div class="section">
    <h2>Especializações / Descrição</h2>
    <div class="descricao">${selecionado.descricao}</div>
  </div>` : ''}

  ${documentos.length > 0 ? `
  <div class="section">
    <h2>Documentos enviados (${documentos.length})</h2>
    <div class="docs">${docsHtml}</div>
  </div>` : ''}

  <p class="footer">
    Gerado em ${new Date().toLocaleString('pt-BR')} · Sistema FEPCSSP ·
    Cadastro: ${selecionado.dataCadastro
      ? new Date(selecionado.dataCadastro).toLocaleDateString('pt-BR')
      : '—'}
  </p>
</body>
</html>`;

  const pw = window.open('', '_blank', 'width=960,height=800');
  if (!pw) { alert('Permita pop-ups para exportar o PDF.'); return; }
  pw.document.write(html);
  pw.document.close();

  // Aguarda todas as imagens carregarem antes de imprimir
  pw.addEventListener('load', () => {
    const imgs = Array.from(pw.document.querySelectorAll('img'));
    if (imgs.length === 0) { pw.print(); return; }

    let pendentes = imgs.filter(img => !img.complete).length;
    if (pendentes === 0) { pw.print(); return; }

    imgs.forEach(img => {
      if (!img.complete) {
        img.onload  = () => { pendentes--; if (pendentes === 0) pw.print(); };
        img.onerror = () => { pendentes--; if (pendentes === 0) pw.print(); };
      }
    });
  });

  setTimeout(() => pw.print(), 5000); // fallback de segurança
};
  // ─── Exportar CSV ─────────────────────────────────────────────────────────
  const handleExportar = () => {
    const csv = [
      'ID,Nome,Email,CPF,CNPJ,Status,Cidade,Estado,Data Cadastro',
      ...filiados.map(f =>
        `${f.systemId},"${f.nome}",${f.email},${f.cpf},${f.cnpj},${f.status},${f.cidade},${f.estado},${f.dataCadastro}`
      ),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `filiados_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // ─── Filtros + Paginação ──────────────────────────────────────────────────
  const filiadosFiltrados = useMemo(() => filiados.filter(f => {
    const matchStatus = filtroStatus === 'todos' || f.status === filtroStatus;
    const matchEstado = filtroEstado === 'todos' || f.estado === filtroEstado;
    const q = busca.trim().toLowerCase();
    const matchBusca = !q ||
      f.nome.toLowerCase().includes(q) ||
      f.email.toLowerCase().includes(q) ||
      f.systemId.toLowerCase().includes(q) ||
      f.nomeResponsavel.toLowerCase().includes(q) ||
      f.cidade.toLowerCase().includes(q);
    return matchStatus && matchEstado && matchBusca;
  }), [filiados, filtroStatus, filtroEstado, busca]);

  const totalPaginas = Math.max(1, Math.ceil(filiadosFiltrados.length / POR_PAGINA));
  const paginaSegura = Math.min(paginaAtual, totalPaginas);

  const filiadosPaginados = useMemo(() => {
    const inicio = (paginaSegura - 1) * POR_PAGINA;
    return filiadosFiltrados.slice(inicio, inicio + POR_PAGINA);
  }, [filiadosFiltrados, paginaSegura]);

  const estados = useMemo(() =>
    Array.from(new Set(filiados.map(f => f.estado))).filter(Boolean).sort(), [filiados]);

  const stats = useMemo(() => ({
    total:     totalRegistros || filiados.length,
    ativos:    filiados.filter(f => f.status === 'ativo').length,
    pendentes: filiados.filter(f => f.status === 'pendente').length,
    inativos:  filiados.filter(f => f.status === 'inativo').length,
  }), [filiados, totalRegistros]);

  // ─── Gera array de páginas visíveis (máx 7 botões) ───────────────────────
  const paginasVisiveis = useMemo(() => {
    const pages: (number | '...')[] = [];
    if (totalPaginas <= 7) {
      for (let i = 1; i <= totalPaginas; i++) pages.push(i);
    } else {
      pages.push(1);
      if (paginaSegura > 3) pages.push('...');
      for (let i = Math.max(2, paginaSegura - 1); i <= Math.min(totalPaginas - 1, paginaSegura + 1); i++) {
        pages.push(i);
      }
      if (paginaSegura < totalPaginas - 2) pages.push('...');
      pages.push(totalPaginas);
    }
    return pages;
  }, [totalPaginas, paginaSegura]);

  // ─── Badge status ─────────────────────────────────────────────────────────
  const getStatusBadge = (status: Filiado['status']) => {
    const badges = {
      ativo:    { dot:'bg-emerald-500', text:'text-emerald-700', label:'Ativo'    },
      inativo:  { dot:'bg-cosmos-300',  text:'text-cosmos-500',  label:'Inativo'  },
      suspenso: { dot:'bg-rose-500',    text:'text-rose-700',    label:'Suspenso' },
      pendente: { dot:'bg-gold-500',    text:'text-gold-700',    label:'Pendente' },
    };
    const b = badges[status];
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-white/60 border border-cosmos-100 ${b.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${b.dot}`} />{b.label}
      </span>
    );
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6 relative">
      <GlassBackdrop />

      {/* HEADER + STATS */}
      <div className={`${glass.card} p-6`}>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-semibold text-cosmos-900 tracking-tight mb-1">Gerenciamento de Filiados</h2>
            <p className="text-sm text-cosmos-500/80">Escolas e profissionais filiados à Federação</p>
          </div>
          <div className="flex gap-2">
            <button onClick={fetchFiliados}
              className={`p-2.5 ${glass.pill} text-cosmos-600 hover:text-cosmos-900 transition-colors`}>
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={handleExportar}
              className={`flex items-center gap-2 px-3 py-2 ${glass.pill} text-cosmos-600 hover:text-cosmos-900 transition-colors text-sm font-medium`}>
              <Download className="w-4 h-4" /> Exportar
            </button>
            <button onClick={() => setModalCadastro(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-2xl bg-cosmos-900 text-white text-sm font-medium hover:bg-cosmos-800 transition-all">
              <Plus className="w-4 h-4" /> Novo Filiado
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label:'Total',     value: stats.total,     alert: false },
            { label:'Ativos',    value: stats.ativos,    alert: false },
            { label:'Pendentes', value: stats.pendentes, alert: stats.pendentes > 0 },
            { label:'Inativos',  value: stats.inativos,  alert: false },
          ].map(s => (
            <div key={s.label} className="relative bg-cosmos-50/60 rounded-2xl p-4 border border-cosmos-100/70">
              {s.alert && (
                <div className="absolute top-3 right-3 w-1.5 h-1.5 rounded-full bg-gold-500 animate-pulse" />
              )}
              <p className={text.label}>{s.label}</p>
              <p className="text-2xl font-semibold text-cosmos-900 mt-1">{s.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* FILTROS */}
      <div className={`${glass.card} p-4`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-cosmos-400" />
            <input type="text" value={buscaInput} onChange={e => setBuscaInput(e.target.value)}
              placeholder="Buscar por nome, email, ID..."
              className="w-full pl-11 pr-4 py-2.5 bg-white/60 border border-cosmos-100 rounded-xl focus:ring-2 focus:ring-gold-400 focus:border-transparent text-cosmos-900 text-sm font-medium placeholder:text-cosmos-400" />
          </div>
          <div className="flex items-center gap-2 bg-white/60 border border-cosmos-100 rounded-xl px-3">
            <Filter className="w-4 h-4 text-cosmos-400" />
            <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
              className="bg-transparent outline-none py-2.5 text-cosmos-900 text-sm font-medium w-full">
              <option value="todos">Todos Status</option>
              <option value="ativo">Ativos</option>
              <option value="pendente">Pendentes</option>
              <option value="inativo">Inativos</option>
              <option value="suspenso">Suspensos</option>
            </select>
          </div>
          <div className="flex items-center gap-2 bg-white/60 border border-cosmos-100 rounded-xl px-3">
            <MapPin className="w-4 h-4 text-cosmos-400" />
            <select value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}
              className="bg-transparent outline-none py-2.5 text-cosmos-900 text-sm font-medium w-full">
              <option value="todos">Todos Estados</option>
              {estados.map(e => <option key={e} value={e}>{e}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* TABELA */}
      <div className={`${glass.card} overflow-hidden`}>
        {loading ? (
          <div className="py-20 flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-gold-600 animate-spin" />
            <p className="text-sm text-cosmos-500 font-medium">Carregando filiados...</p>
          </div>
        ) : error ? (
          <div className="py-20 text-center">
            <AlertTriangle className="w-10 h-10 text-rose-400 mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-rose-600 font-medium text-sm">{error}</p>
            <button onClick={fetchFiliados}
              className="mt-4 px-4 py-2 bg-cosmos-900 text-white rounded-xl text-sm font-medium">
              Tentar novamente
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-cosmos-100">
                    {['ID / Nome','Contato','Localização','Status','Cadastro',''].map(h => (
                      <th key={h} className={`px-6 py-3.5 text-xs font-semibold text-cosmos-400 uppercase tracking-wide ${h===''?'text-right':'text-left'}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-cosmos-100/60">
                  {filiadosPaginados.map(f => (
                    <tr key={f.id} className="hover:bg-cosmos-50/40 transition-colors">
                      <td className="px-6 py-3.5">
                        <div className="flex items-center gap-3">
                          <FiliadoAvatar key={f.logoUrl ?? f.id} filiado={f} size="sm" />
                          <div>
                            <p className="font-medium text-cosmos-900 text-sm">{f.nome}</p>
                            <p className="text-xs font-mono text-cosmos-400">{f.systemId}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-3.5">
                        <div className="space-y-0.5">
                          <p className="text-xs text-cosmos-600 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-cosmos-300" />{f.email||'—'}</p>
                          <p className="text-xs text-cosmos-500 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-cosmos-300" />{f.telefone||'—'}</p>
                        </div>
                      </td>
                      <td className="px-6 py-3.5">
                        <div className="flex items-center gap-1.5">
                          <MapPin className="w-3.5 h-3.5 text-cosmos-300" />
                          <span className="text-xs text-cosmos-600">
                            {f.cidade && f.estado ? `${f.cidade}, ${f.estado}` : f.cidade || f.estado || '—'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-3.5">{getStatusBadge(f.status)}</td>
                      <td className="px-6 py-3.5">
                        <span className="text-xs text-cosmos-500">
                          {f.dataCadastro ? new Date(f.dataCadastro).toLocaleDateString('pt-BR') : '—'}
                        </span>
                      </td>
                      <td className="px-6 py-3.5">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => abrirModal(f)}
                            className="p-1.5 text-cosmos-400 hover:text-cosmos-900 hover:bg-cosmos-50 rounded-lg transition-colors" title="Ver detalhes">
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleToggleEscola(f)}
                            title={f.isSchool ? 'Remover do portal de escolas' : 'Adicionar ao portal de escolas'}
                            className={`p-1.5 rounded-lg transition-colors ${
                              f.isSchool
                                ? 'text-gold-500 hover:bg-gold-50'
                                : 'text-cosmos-200 hover:text-gold-400 hover:bg-gold-50'
                            }`}
                          >
                            <Star className={`w-4 h-4 ${f.isSchool ? 'fill-gold-400' : ''}`} />
                          </button>
                          <button onClick={() => abrirEditar(f)}
                            className="p-1.5 text-cosmos-400 hover:text-cosmos-900 hover:bg-cosmos-50 rounded-lg transition-colors" title="Editar">
                            <Edit className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDeletar(f.id, f.nome)} disabled={salvando}
                            className="p-1.5 text-cosmos-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors disabled:opacity-50" title="Excluir">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filiadosFiltrados.length === 0 && !loading && (
              <div className="py-12 text-center">
                <UserCog className="w-12 h-12 text-cosmos-200 mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-sm text-cosmos-500 font-medium">Nenhum filiado encontrado</p>
              </div>
            )}

            {/* ── RODAPÉ COM PAGINAÇÃO ─────────────────────────────────── */}
            <div className="px-6 py-4 border-t border-cosmos-100 flex items-center justify-between gap-4">
              <p className="text-xs text-cosmos-500 whitespace-nowrap">
                Mostrando{' '}
                <span className="font-semibold text-cosmos-900">
                  {filiadosFiltrados.length === 0 ? 0 : (paginaSegura - 1) * POR_PAGINA + 1}–{Math.min(paginaSegura * POR_PAGINA, filiadosFiltrados.length)}
                </span>
                {' '}de{' '}
                <span className="font-semibold text-cosmos-900">{filiadosFiltrados.length}</span>
                {filiadosFiltrados.length !== stats.total && (
                  <span className="text-cosmos-300"> (filtrado de {stats.total})</span>
                )}
              </p>

              {totalPaginas > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPaginaAtual(p => Math.max(1, p - 1))}
                    disabled={paginaSegura === 1}
                    className="p-2 rounded-lg border border-cosmos-100 text-cosmos-500 hover:bg-cosmos-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  {paginasVisiveis.map((p, i) =>
                    p === '...' ? (
                      <span key={`ellipsis-${i}`} className="px-2 py-1 text-cosmos-300 text-sm select-none">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPaginaAtual(p as number)}
                        className={`min-w-[32px] h-8 px-2 rounded-lg text-xs font-semibold border transition-colors
                          ${paginaSegura === p
                            ? 'bg-cosmos-900 text-white border-cosmos-900'
                            : 'border-cosmos-100 text-cosmos-600 hover:bg-cosmos-50'
                          }`}
                      >
                        {p}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => setPaginaAtual(p => Math.min(totalPaginas, p + 1))}
                    disabled={paginaSegura === totalPaginas}
                    className="p-2 rounded-lg border border-cosmos-100 text-cosmos-500 hover:bg-cosmos-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── MODAL VER ─────────────────────────────────────────────────────── */}
      {modalVer && selecionado && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">

            <div className="p-6 border-b border-white/10 bg-cosmos-700 rounded-t-2xl flex-shrink-0">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  {selecionado.logoUrl && !logoError ? (
                    <img
                      src={selecionado.logoUrl}
                      alt={selecionado.nome}
                      className="w-14 h-14 rounded-2xl object-cover border border-white/20 bg-white flex-shrink-0"
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-2xl bg-gradient-gold flex items-center justify-center text-cosmos-900 font-semibold text-xl flex-shrink-0">
                      {selecionado.nome.charAt(0)}
                    </div>
                  )}
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-1.5 tracking-tight">{selecionado.nome}</h3>
                    <div className="flex items-center gap-1.5 bg-white/15 rounded-lg px-2.5 py-1 w-fit">
                      <Hash className="w-3.5 h-3.5 text-gold-400" />
                      <span className="font-mono font-medium text-white/90 text-xs">{selecionado.systemId||'—'}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {getStatusBadge(selecionado.status)}
                  <button onClick={() => setModalVer(false)}
                    className="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg text-white/80 hover:text-white transition-colors">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex gap-1 mt-4">
{([
  ['info',       'Informações'],
  ['documentos', 'Documentos'],
  ['pagamento',  'Pagamento'],
] as [ModalTab, string][]).map(([tab, label]) => (                  <button key={tab} onClick={() => setModalTab(tab)}
                    className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors
                      ${modalTab === tab
                        ? 'bg-white/10 text-white border border-gold-400/30'
                        : 'text-white/50 hover:text-white/80 border border-transparent'}`}>
                    {label}
                    {tab === 'documentos' && documentos.length > 0 && (
                      <span className="ml-1.5 text-[10px] bg-white/15 px-1.5 py-0.5 rounded-full">
                        {documentos.length}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {modalTab === 'info' && (
                <>
                  <div>
                    <h4 className="flex items-center gap-2 text-sm font-black text-gray-500 uppercase tracking-wider mb-3">
                      <User className="w-4 h-4" /> Identificação
                    </h4>
                    <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-xl p-4">
                      {[
                        { label:'Nome do Responsável', value: selecionado.nomeResponsavel||'—'           },
                        { label:'ID do Sistema',       value: selecionado.systemId||'—',    mono:true    },
                        { label:'ID Interno (UUID)',   value: selecionado.id,               mono:true    },
                        { label:'CPF',                 value: selecionado.cpf||'—'                       },
                        { label:'CNPJ',                value: selecionado.cnpj||'—'                      },
                        { label:'Registro Fepcssp',    value: selecionado.registroFepcssp||'—'           },
                        { label:'Identidade internacional', value: selecionado.identidadeInternacional||'—', mono:true },
                      ].map(f => (
                        <div key={f.label}>
                          <p className="text-xs text-gray-500 mb-0.5">{f.label}</p>
                          <p className={`font-bold text-gray-900 text-sm ${f.mono?'font-mono':''}`}>{f.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="flex items-center gap-2 text-sm font-black text-gray-500 uppercase tracking-wider mb-3">
                      <Mail className="w-4 h-4" /> Contato
                    </h4>
                    <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-xl p-4">
                      {[
                        { label:'E-mail',   value: selecionado.email    || '—' },
                        { label:'Telefone', value: selecionado.telefone || '—' },
                      ].map(f => (
                        <div key={f.label}>
                          <p className="text-xs text-gray-500 mb-0.5">{f.label}</p>
                          <p className="font-bold text-gray-900 text-sm">{f.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="flex items-center gap-2 text-sm font-black text-gray-500 uppercase tracking-wider mb-3">
                      <Home className="w-4 h-4" /> Localização
                    </h4>
                    <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-xl p-4">
                      {[
                        { label:'Endereço', value: selecionado.endereco || '—' },
                        { label:'CEP',      value: selecionado.cep      || '—' },
                        { label:'Cidade',   value: selecionado.cidade   || '—' },
                        { label:'Estado',   value: selecionado.estado   || '—' },
                      ].map(f => (
                        <div key={f.label}>
                          <p className="text-xs text-gray-500 mb-0.5">{f.label}</p>
                          <p className="font-bold text-gray-900 text-sm">{f.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {selecionado.descricao && (
                    <div>
                      <h4 className="flex items-center gap-2 text-sm font-black text-gray-500 uppercase tracking-wider mb-3">
                        <FileText className="w-4 h-4" /> Especializações
                      </h4>
                      <div className="bg-gray-50 rounded-xl p-4">
                        <p className="text-sm text-gray-900">{selecionado.descricao}</p>
                      </div>
                    </div>
                  )}

                  {(selecionado.metadata?.funcao != null ||
                    selecionado.metadata?.experienciaAnos != null ||
                    selecionado.metadata?.dataInicioAtividade != null) && (
                    <div>
                      <h4 className="flex items-center gap-2 text-sm font-black text-gray-500 uppercase tracking-wider mb-3">
                        <User className="w-4 h-4" /> Atuação Profissional
                      </h4>
                      <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-xl p-4">
                        {[
                          { label: 'Função',               value: selecionado.metadata?.funcao                                                                                          },
                          { label: 'Anos de Experiência',  value: selecionado.metadata?.experienciaAnos != null ? `${selecionado.metadata.experienciaAnos} anos` : null                },
                          { label: 'Início na Área',       value: selecionado.metadata?.dataInicioAtividade ? new Date(selecionado.metadata.dataInicioAtividade).toLocaleDateString('pt-BR') : null },
                          { label: 'Complemento Endereço', value: selecionado.metadata?.complemento                                                                                     },
                        ].filter(f => f.value).map(f => (
                          <div key={f.label}>
                            <p className="text-xs text-gray-500 mb-0.5">{f.label}</p>
                            <p className="font-bold text-gray-900 text-sm">{f.value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selecionado.logoUrl && !logoError && (
                    <div>
                      <h4 className="flex items-center gap-2 text-sm font-black text-gray-500 uppercase tracking-wider mb-3">
                        <Building2 className="w-4 h-4" /> Logomarca
                      </h4>
                      <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-center">
                        <img
                          src={selecionado.logoUrl}
                          alt={`Logo de ${selecionado.nome}`}
                          className="max-h-48 max-w-full rounded-xl object-contain border border-gray-200 shadow-sm"
                          onError={() => setLogoError(true)}
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar className="w-4 h-4" />
                    Cadastrado em {selecionado.dataCadastro
                      ? new Date(selecionado.dataCadastro).toLocaleDateString('pt-BR', { day:'2-digit', month:'long', year:'numeric' })
                      : '—'}
                  </div>
                </>
              )}

              {modalTab === 'documentos' && (
                <div>
                  {loadingDocs ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-3">
                      <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                      <p className="text-sm text-gray-500">Carregando documentos...</p>
                    </div>
                  ) : documentos.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-3">
                        <FileText className="w-7 h-7 text-gray-300" />
                      </div>
                      <p className="text-sm font-semibold text-gray-600">Nenhum documento enviado</p>
                      <p className="text-xs text-gray-400 mt-1">
                        Os documentos aparecem aqui após o filiado enviá-los no formulário de cadastro.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-sm text-gray-500">
                        {documentos.length} documento{documentos.length !== 1 ? 's' : ''} enviado{documentos.length !== 1 ? 's' : ''}
                      </p>
                      {documentos.map(doc => (
                        <div key={doc.id} className="border border-gray-200 rounded-xl overflow-hidden">
                          {isImage(doc.mimeType) && (
                            <div className="bg-gray-50 flex items-center justify-center p-4 border-b border-gray-100">
                              <img src={doc.url} alt={doc.fileName}
                                className="max-h-48 max-w-full object-contain rounded-lg"
                                onError={e => {
                                  const el = e.target as HTMLImageElement;
                                  el.parentElement!.innerHTML = '<p class="text-xs text-gray-400 py-4">Não foi possível carregar a imagem</p>';
                                }} />
                            </div>
                          )}
                          {doc.mimeType === 'application/pdf' && (
                            <div className="bg-red-50 flex items-center justify-center py-6 border-b border-gray-100">
                              <div className="text-center">
                                <FileText className="w-10 h-10 text-red-400 mx-auto mb-1" />
                                <p className="text-xs text-red-600 font-medium">PDF</p>
                              </div>
                            </div>
                          )}
                          <div className="p-4 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                              {isImage(doc.mimeType)
                                ? <Image className="w-5 h-5 text-blue-500 flex-shrink-0" />
                                : <FileText className="w-5 h-5 text-red-500 flex-shrink-0" />
                              }
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-gray-800 truncate">
                                  {doc.description || doc.fileName}
                                </p>
                                <p className="text-xs text-gray-400 mt-0.5">
                                  <span className="font-medium text-blue-600">
                                    {DOC_TYPE_LABEL[doc.documentType] ?? doc.documentType}
                                  </span>
                                  {' · '}{formatFileSize(doc.fileSize)}
                                  {' · '}{new Date(doc.uploadedAt).toLocaleDateString('pt-BR')}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <a href={doc.url} target="_blank" rel="noopener noreferrer"
                                className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                                title="Abrir">
                                <ExternalLink className="w-4 h-4" />
                              </a>
                              <button
                                onClick={() => handleDeletarDocumento(doc.id)}
                                className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                                title="Excluir documento">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {modalTab === 'pagamento' && (
  <div className="space-y-5">

    {/* Feedback */}
    {erroPagamento && (
      <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
        <AlertCircle className="w-5 h-5 flex-shrink-0" />
        <p className="text-sm font-semibold">{erroPagamento}</p>
      </div>
    )}
    {sucPagamento && (
      <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700">
        <CheckCircle className="w-5 h-5 flex-shrink-0" />
        <p className="text-sm font-semibold">{sucPagamento}</p>
      </div>
    )}

    {/* Status atual */}
    <div className="bg-gray-50 rounded-xl p-4 space-y-2">
      <p className="text-xs font-black text-gray-400 uppercase tracking-wider mb-3">Status do Pagamento</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-xs text-gray-500">Link atual</p>
          {(selecionado as any).paymentLink ? (
            <a href={(selecionado as any).paymentLink} target="_blank" rel="noopener noreferrer"
              className="text-sm font-bold text-blue-600 underline truncate block">
              Ver link
            </a>
          ) : (
            <p className="text-sm font-bold text-gray-400">Não definido</p>
          )}
        </div>
        <div>
          <p className="text-xs text-gray-500">Valor</p>
          <p className="text-sm font-bold text-gray-900">
            {(selecionado as any).paymentValue
              ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format((selecionado as any).paymentValue)
              : '—'}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Confirmado em</p>
          <p className="text-sm font-bold text-gray-900">
            {(selecionado as any).paymentConfirmedAt
              ? new Date((selecionado as any).paymentConfirmedAt).toLocaleDateString('pt-BR')
              : '—'}
          </p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Acesso liberado</p>
          <p className={`text-sm font-bold ${(selecionado as any).accessVideosReleased ? 'text-green-600' : 'text-gray-400'}`}>
            {(selecionado as any).accessVideosReleased ? 'Sim ✓' : 'Não'}
          </p>
        </div>
      </div>
    </div>

    {/* Formulário gerar cobrança */}
    <div className="border-t border-gray-100 pt-4">
      <p className="text-xs font-black text-gray-400 uppercase tracking-wider mb-3">Gerar cobrança no Mercado Pago</p>
      <div className="space-y-3">
        <div>
          <label className={labelCls}>Valor (R$)</label>
          <input
            type="text"
            value={paymentValueInput}
            onChange={e => setPaymentValueInput(e.target.value.replace(/[^\d,\.]/g, ''))}
            placeholder="150,00"
            className={inputCls}
          />
        </div>
        <button
          onClick={handleSalvarPagamento}
          disabled={salvandoPagamento}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-indigo-600 text-white font-bold hover:bg-indigo-700 disabled:opacity-60 transition-colors"
        >
          {salvandoPagamento ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Gerar e Enviar para Candidato
        </button>
      </div>
    </div>

    {/* Confirmar pagamento */}
    

    {/* Link para portal de verificação */}
    <div className="border-t border-gray-100 pt-4">
      <p className="text-xs font-black text-gray-400 uppercase tracking-wider mb-3">Portal do Candidato</p>
      <a
        href={`/verificar/${encodeURIComponent(selecionado.systemId)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-blue-300 text-blue-600 font-bold hover:bg-blue-50 transition-colors"
      >
        <ExternalLink className="w-4 h-4" />
        Abrir Página de Verificação do Candidato
      </a>
      <p className="text-xs text-gray-400 text-center mt-1">
        Protocolo: <span className="font-mono font-bold">{selecionado.systemId}</span>
      </p>
    </div>
  </div>
)}
            </div>
             
            <div className="px-6 py-4 border-t border-cosmos-100 flex flex-wrap items-center gap-2 flex-shrink-0">
              <button
                onClick={handleExportarPDFModal}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-cosmos-200 text-cosmos-600 text-sm font-medium hover:bg-cosmos-50 transition-colors"
              >
                <Download className="w-3.5 h-3.5" /> Exportar PDF
              </button>
              <button onClick={() => setModalVer(false)}
                className="px-3 py-2 rounded-lg border border-cosmos-200 text-cosmos-600 text-sm font-medium hover:bg-cosmos-50 transition-colors">
                Fechar
              </button>

              <div className="ml-auto flex items-center gap-2">
                {(selecionado.status === 'pendente' || (selecionado.status as string) === 'aguardando_pagamento') && (<>
                  <button
                    onClick={() => { setModalRejeitar(selecionado); setModalVer(false); setMotivoRejeicao(''); }}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-rose-50 text-rose-600 text-sm font-medium hover:bg-rose-100 transition-colors">
                    <XCircle className="w-4 h-4" /> Rejeitar
                  </button>
                  <button onClick={() => handleAprovar(selecionado.id)} disabled={salvando}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors disabled:opacity-60">
                    {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    Aprovar
                  </button>
                </>)}
                {selecionado.status === 'ativo' && selecionado.telefone && (
                  <button onClick={() => handleWhatsApp(selecionado)}
                    className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-white text-sm font-medium transition-colors"
                    style={{ backgroundColor: '#25D366' }}>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    Enviar pelo WhatsApp
                  </button>
                )}
                <button onClick={() => { setModalVer(false); abrirEditar(selecionado); }}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-cosmos-900 text-white text-sm font-medium hover:bg-cosmos-800 transition-colors">
                  <Edit className="w-4 h-4" /> Editar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL REJEITAR ────────────────────────────────────────────────── */}
      {modalRejeitar && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="text-xl font-black text-gray-900">Rejeitar Filiado</h3>
                <p className="text-sm text-gray-500 mt-0.5">{modalRejeitar.nome}</p>
              </div>
              <button onClick={() => setModalRejeitar(null)}
                className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Motivo da Rejeição *
              </label>
              <textarea value={motivoRejeicao} onChange={e => setMotivoRejeicao(e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 resize-none text-gray-900 font-semibold"
                placeholder="Informe o motivo da rejeição (será enviado por e-mail ao solicitante)..." />
              <p className={`text-xs mt-1 text-right ${motivoRejeicao.trim().length < 10 ? 'text-red-400' : 'text-gray-400'}`}>
                {motivoRejeicao.trim().length}/10 caracteres mínimos
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button onClick={() => setModalRejeitar(null)}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={handleRejeitar} disabled={salvando}
                className="flex-1 px-4 py-3 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 flex items-center justify-center gap-2 disabled:opacity-60">
                {salvando ? <Loader2 className="w-5 h-5 animate-spin" /> : <XCircle className="w-5 h-5" />}
                Confirmar Rejeição
              </button>
            </div>
          </div>
        </div>
      )}
       
      {/* ── MODAL EDITAR ──────────────────────────────────────────────────── */}
      {modalEditar && selecionado && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">

            <div className="flex-shrink-0">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div>
                  <h3 className="text-xl font-black text-gray-900">Editar Filiado</h3>
                  <p className="text-sm font-mono text-blue-600">{selecionado.systemId}</p>
                </div>
                <button onClick={() => setModalEditar(false)}
                  className="p-2 hover:bg-gray-200 rounded-lg text-gray-500 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex border-b border-gray-200 bg-white px-6">
                {([
                  ['dados',      'Dados Cadastrais', Edit],
                  ['documentos', 'Documentos',       FileText],
                ] as [EditTab, string, any][]).map(([tab, label, Icon]) => (
                  <button
                    key={tab}
                    onClick={() => setModalEditarTab(tab)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 transition-colors -mb-px
                      ${modalEditarTab === tab
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                    {tab === 'documentos' && documentos.length > 0 && (
                      <span className="ml-1 text-[10px] bg-blue-100 text-blue-700 font-bold px-1.5 py-0.5 rounded-full">
                        {documentos.length}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-5">

              {/* ── ABA DADOS ─────────────────────────────────────────── */}
              {modalEditarTab === 'dados' && (
                <>
                  {erroEdit && (
                    <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      <p className="text-sm font-semibold">{erroEdit}</p>
                    </div>
                  )}

                  {/* ── Bloco: Identificação ── */}
                  <div className="pb-1">
                    <p className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-3">Identificação</p>
                    <div className="space-y-4">
                      <div>
                        <label className={labelCls}>Nome / Escola</label>
                        <input type="text" value={formEdit.nome??''} onChange={e => setFormEdit(p=>({...p,nome:e.target.value}))} className={inputCls} />
                      </div>

                      {/* ✅ v4.4: Nome do Responsável */}
                      <div>
                        <label className={labelCls}>Nome do Responsável</label>
                        <input
                          type="text"
                          value={formEdit.nomeResponsavel ?? ''}
                          onChange={e => setFormEdit(p => ({ ...p, nomeResponsavel: e.target.value }))}
                          className={inputCls}
                          placeholder="Nome completo do responsável"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className={labelCls}>CPF</label>
                          <input type="text" value={formEdit.cpf??''} onChange={e => setFormEdit(p=>({...p,cpf:e.target.value.replace(/\D/g,'').slice(0,11)}))} className={inputCls} placeholder="Somente números" />
                        </div>
                        <div>
                          <label className={labelCls}>CNPJ</label>
                          <input type="text" value={formEdit.cnpj??''} onChange={e => setFormEdit(p=>({...p,cnpj:e.target.value.replace(/\D/g,'').slice(0,14)}))} className={inputCls} placeholder="Somente números" />
                        </div>
                      </div>

                      {/* ✅ v4.4: Data de nascimento */}
                      <div>
                        <label className={labelCls}>Data de Nascimento</label>
                        <input
                          type="date"
                          value={formEdit.dataNascimento ?? ''}
                          onChange={e => setFormEdit(p => ({ ...p, dataNascimento: e.target.value }))}
                          className={inputCls}
                        />
                      </div>

                      <div>
                        <label className={labelCls}>Nº FEPCSSP</label>
                        <input
                          type="text"
                          value={formEdit.registroFepcssp ?? ''}
                          onChange={e => setFormEdit(p => ({ ...p, registroFepcssp: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                          className={inputCls}
                          placeholder="000001"
                          maxLength={6}
                        />
                        <p className="text-xs text-gray-400 mt-1">Número de registro na FEPCSSP (somente dígitos)</p>
                      </div>
                      <div>
                        <label className={labelCls}>Identidade internacional</label>
                        <input
                          type="text"
                          value={formEdit.identidadeInternacional ?? ''}
                          onChange={e => setFormEdit(p => ({
                            ...p,
                            identidadeInternacional: e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 15),
                          }))}
                          className={inputCls}
                          placeholder="AB12CD34EF56GH7"
                          maxLength={15}
                        />
                        <p className="text-xs text-gray-400 mt-1">15 caracteres, somente letras e números</p>
                      </div>
                    </div>
                  </div>

                  {/* ── Bloco: Contato ── */}
                  <div className="border-t border-gray-100 pt-4">
                    <p className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-3">Contato</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={labelCls}>E-mail</label>
                        <input type="email" value={formEdit.email??''} onChange={e => setFormEdit(p=>({...p,email:e.target.value}))} className={inputCls} />
                      </div>
                      <div>
                        <label className={labelCls}>Telefone</label>
                        <input type="text" value={formEdit.telefone??''} onChange={e => setFormEdit(p=>({...p,telefone:e.target.value}))} className={inputCls} />
                      </div>
                    </div>
                  </div>

                  {/* ── Bloco: Endereço ── */}
                  <div className="border-t border-gray-100 pt-4">
                    <p className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-3">Endereço</p>
                    <div className="space-y-4">
                      <div>
                        <label className={labelCls}>Endereço</label>
                        <input type="text" value={formEdit.endereco??''} onChange={e => setFormEdit(p=>({...p,endereco:e.target.value}))} className={inputCls} />
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className={labelCls}>CEP</label>
                          <input type="text" value={formEdit.cep??''} onChange={e => setFormEdit(p=>({...p,cep:e.target.value.replace(/\D/g,'').slice(0,8)}))} className={inputCls} />
                        </div>
                        <div>
                          <label className={labelCls}>Cidade</label>
                          <input type="text" value={formEdit.cidade??''} onChange={e => setFormEdit(p=>({...p,cidade:e.target.value}))} className={inputCls} />
                        </div>
                        <div>
                          <label className={labelCls}>UF</label>
                          <input type="text" value={formEdit.estado??''} onChange={e => setFormEdit(p=>({...p,estado:e.target.value.slice(0,2).toUpperCase()}))} className={inputCls} maxLength={2} />
                        </div>
                      </div>

                      {/* ✅ v4.4: Complemento */}
                      <div>
                        <label className={labelCls}>Complemento</label>
                        <input
                          type="text"
                          value={(formEdit as any).complemento ?? ''}
                          onChange={e => setFormEdit(p => ({ ...p, complemento: e.target.value }))}
                          className={inputCls}
                          placeholder="Apto, Sala, Bloco..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* ── Bloco: Outros ── */}
                  <div className="border-t border-gray-100 pt-4">
                    <p className="text-[11px] font-black text-gray-400 uppercase tracking-widest mb-3">Outros</p>
                    <div className="space-y-4">
                      <div>
                        <label className={labelCls}>Status</label>
                        <select value={formEdit.status??'pendente'} onChange={e => setFormEdit(p=>({...p,status:e.target.value as Filiado['status']}))} className={inputCls}>
                          <option value="ativo">Ativo</option>
                          <option value="pendente">Pendente</option>
                          <option value="inativo">Inativo</option>
                          <option value="suspenso">Suspenso</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>Especializações / Descrição</label>
                        <textarea rows={3} value={formEdit.descricao??''} onChange={e => setFormEdit(p=>({...p,descricao:e.target.value}))} className={`${inputCls} resize-none`} />
                      </div>
                      <div>
                        <label className={labelCls}>Site / Instagram</label>
                        <input
                          type="text"
                          value={formEdit.website??''}
                          onChange={e => setFormEdit(p=>({...p, website:e.target.value}))}
                          className={inputCls}
                          placeholder="https://www.instagram.com/suaescola ou https://seusite.com.br"
                        />
                        <p className="text-xs text-gray-400 mt-1">Cole o link do Instagram, site ou WhatsApp da escola</p>
                      </div>
                      <div className="flex items-center justify-between p-4 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50">
                        <div>
                          <p className="text-sm font-bold text-gray-800">Exibir no Portal de Escolas?</p>
                          <p className="text-xs text-gray-500 mt-0.5">Marque para aparecer na página pública de escolas</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setFormEdit(p => ({ ...p, isSchool: !p.isSchool }))}
                          className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${formEdit.isSchool ? 'bg-amber-500' : 'bg-gray-300'}`}
                        >
                          <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formEdit.isSchool ? 'translate-x-6' : 'translate-x-0'}`} />
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* ── ABA DOCUMENTOS ────────────────────────────────────── */}
              {modalEditarTab === 'documentos' && (
                <div className="space-y-6">

                  <div>
                    <h4 className="text-xs font-black text-gray-500 uppercase tracking-wider mb-3">
                      Documentos enviados
                    </h4>

                    {loadingDocs ? (
                      <div className="flex items-center justify-center py-8 gap-3">
                        <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                        <p className="text-sm text-gray-500">Carregando...</p>
                      </div>
                    ) : documentos.length === 0 ? (
                      <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
                        <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">Nenhum documento ainda</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {documentos.map(doc => (
                          <div key={doc.id}
                            className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors">
                            <div className="w-12 h-12 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0 bg-gray-100 flex items-center justify-center">
                              {isImage(doc.mimeType) ? (
                                <img src={doc.url} alt="" className="w-full h-full object-cover"
                                  onError={e => { (e.target as HTMLImageElement).style.display='none'; }} />
                              ) : (
                                <FileText className="w-5 h-5 text-red-400" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-800 truncate">
                                {doc.description || doc.fileName}
                              </p>
                              <p className="text-xs text-gray-400 mt-0.5">
                                <span className="font-medium text-blue-600">
                                  {DOC_TYPE_LABEL[doc.documentType] ?? doc.documentType}
                                </span>
                                {' · '}{formatFileSize(doc.fileSize)}
                                {' · '}{new Date(doc.uploadedAt).toLocaleDateString('pt-BR')}
                              </p>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <a href={doc.url} target="_blank" rel="noopener noreferrer"
                                className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                                title="Abrir">
                                <ExternalLink className="w-4 h-4" />
                              </a>
                              <button
                                onClick={() => handleDeletarDocumento(doc.id)}
                                className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                                title="Excluir documento">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <h4 className="text-xs font-black text-gray-500 uppercase tracking-wider mb-3">
                      Adicionar novo documento
                    </h4>

                    <div className="space-y-3 p-4 bg-gray-50 rounded-xl border border-gray-200">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={labelCls}>Tipo do documento</label>
                          <select
                            value={tipoDocEdit}
                            onChange={e => setTipoDocEdit(e.target.value)}
                            className={inputCls}
                          >
                            {Object.entries(DOC_TYPE_LABEL).map(([v, l]) => (
                              <option key={v} value={v}>{l}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className={labelCls}>Descrição (opcional)</label>
                          <input
                            type="text"
                            value={descricaoDocEdit}
                            onChange={e => setDescricaoDocEdit(e.target.value)}
                            placeholder={`Ex: Logo da escola`}
                            className={inputCls}
                          />
                        </div>
                      </div>

                      {!slotEditDoc.arquivo ? (
                        <label className={`flex flex-col items-center justify-center py-8 rounded-xl border-2 border-dashed cursor-pointer transition-colors
                          ${slotEditDoc.enviado ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50/30'}`}>
                          <div className="w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center mb-2 shadow-sm">
                            <Upload className="w-5 h-5 text-gray-400" />
                          </div>
                          <p className="text-sm font-semibold text-gray-600">Clique para selecionar</p>
                          <p className="text-xs text-gray-400 mt-0.5">JPG, PNG, PDF — máx. 5 MB</p>
                          <input type="file" accept="image/*,.pdf" className="hidden"
                            onChange={e => { const f = e.target.files?.[0]; if (f) selecionarArquivoEdit(f); }} />
                        </label>
                      ) : (
                        <div className={`rounded-xl border-2 overflow-hidden
                          ${slotEditDoc.enviado ? 'border-green-400 bg-green-50' : 'border-blue-300 bg-blue-50/30'}`}>
                          {slotEditDoc.preview && (
                            <div className="flex items-center justify-center p-4 border-b border-gray-100 bg-white">
                              <img src={slotEditDoc.preview} alt="Preview"
                                className="max-h-40 max-w-full object-contain rounded-lg" />
                            </div>
                          )}
                          <div className="flex items-center gap-3 px-4 py-3">
                            {slotEditDoc.enviado
                              ? <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                              : <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                            }
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-800 truncate">
                                {slotEditDoc.arquivo.name}
                              </p>
                              <p className="text-xs text-gray-400">
                                {(slotEditDoc.arquivo.size / 1024).toFixed(0)} KB
                              </p>
                            </div>
                            {!slotEditDoc.enviado && (
                              <button
                                onClick={() => {
                                  if (slotEditDoc.preview) URL.revokeObjectURL(slotEditDoc.preview);
                                  setSlotEditDoc(slotVazio());
                                }}
                                className="p-1.5 rounded-lg bg-red-100 text-red-500 hover:bg-red-200 transition-colors flex-shrink-0">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          {slotEditDoc.erro && (
                            <div className="px-4 pb-3">
                              <p className="text-xs text-red-500 flex items-center gap-1">
                                <AlertCircle className="w-3.5 h-3.5" /> {slotEditDoc.erro}
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {slotEditDoc.arquivo && !slotEditDoc.enviado && (
                        <button
                          onClick={handleUploadDocEdit}
                          disabled={uploadingEditDoc}
                          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-60 transition-colors"
                        >
                          {uploadingEditDoc
                            ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</>
                            : <><Upload className="w-4 h-4" /> Enviar documento</>
                          }
                        </button>
                      )}

                      {slotEditDoc.enviado && (
                        <p className="text-center text-sm text-green-600 font-bold flex items-center justify-center gap-2">
                          <CheckCircle className="w-4 h-4" /> Documento enviado com sucesso!
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex gap-3 flex-shrink-0">
              <button onClick={() => setModalEditar(false)}
                className="flex-1 px-4 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50">
                Cancelar
              </button>
              {modalEditarTab === 'dados' && (
                <button onClick={handleSalvarEdicao} disabled={salvando}
                  className="flex-1 px-4 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 flex items-center justify-center gap-2 disabled:opacity-60">
                  {salvando ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                  {salvando ? 'Salvando...' : 'Salvar alterações'}
                </button>
              )}
              {modalEditarTab === 'documentos' && (
                <button onClick={() => fetchDocumentos(selecionado.id)} disabled={loadingDocs}
                  className="flex-1 px-4 py-3 rounded-xl bg-gray-100 text-gray-700 font-bold hover:bg-gray-200 flex items-center justify-center gap-2 disabled:opacity-60">
                  {loadingDocs ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Atualizar lista
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL CADASTRO */}
      {modalCadastro && (
        <ModalCadastroFiliado
          onFechar={() => setModalCadastro(false)}
          onSucesso={() => { setModalCadastro(false); fetchFiliados(); }}
        />
      )}
    </div>
  );
};

export default GerenciamentoProfissionais;
